import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { budgetAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Loader2, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';

const CATEGORIES = ['general', 'materials', 'labor', 'fixtures', 'furniture', 'electrical', 'plumbing', 'painting', 'flooring', 'other'];
const STATUSES = ['planned', 'ordered', 'received', 'installed', 'paid'];

export default function ProjectBudget({ projectId }) {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    description: '', category: 'general', estimated_cost: '', actual_cost: '', vendor: '', status: 'planned'
  });

  const fetchBudget = useCallback(async () => {
    try {
      const res = await budgetAPI.get(projectId);
      setBudget(res.data);
    } catch (err) {
      console.error('Failed to fetch budget:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchBudget(); }, [fetchBudget]);

  const resetForm = () => setForm({ description: '', category: 'general', estimated_cost: '', actual_cost: '', vendor: '', status: 'planned' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = { ...form, estimated_cost: parseFloat(form.estimated_cost) || 0, actual_cost: parseFloat(form.actual_cost) || 0 };
      if (editingItem) {
        await budgetAPI.updateItem(editingItem.budget_item_id, payload);
      } else {
        await budgetAPI.addItem(projectId, payload);
      }
      setDialogOpen(false);
      setEditingItem(null);
      resetForm();
      fetchBudget();
    } catch (err) {
      console.error('Failed to save budget item:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await budgetAPI.deleteItem(itemId);
      fetchBudget();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      description: item.description, category: item.category, estimated_cost: String(item.estimated_cost),
      actual_cost: String(item.actual_cost), vendor: item.vendor || '', status: item.status
    });
    setDialogOpen(true);
  };

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  const summary = budget?.summary || { total_estimated: 0, total_actual: 0, variance: 0 };
  const items = budget?.items || [];

  return (
    <div className="space-y-6" data-testid="project-budget">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Estimated Budget</p>
                <p className="text-2xl font-bold" data-testid="budget-estimated">{summary.total_estimated.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Actual Spent</p>
                <p className="text-2xl font-bold" data-testid="budget-actual">{summary.total_actual.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Variance</p>
                <p className={`text-2xl font-bold ${summary.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="budget-variance">
                  {summary.variance >= 0 ? '+' : ''}{summary.variance.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
              <TrendingUp className={`h-8 w-8 ${summary.variance >= 0 ? 'text-emerald-600/30' : 'text-red-600/30'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Budget Line Items</CardTitle>
          {canEdit && (
            <Button size="sm" onClick={() => { resetForm(); setEditingItem(null); setDialogOpen(true); }} data-testid="add-budget-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No budget items yet</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Estimated</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => (
                    <TableRow key={item.budget_item_id} data-testid={`budget-item-${item.budget_item_id}`}>
                      <TableCell className="font-medium">{item.description}</TableCell>
                      <TableCell><span className="capitalize text-sm">{item.category}</span></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.vendor || '—'}</TableCell>
                      <TableCell className="text-right">{item.estimated_cost.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right">{item.actual_cost.toLocaleString('en-IN')}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                          item.status === 'installed' ? 'bg-blue-100 text-blue-800' :
                          item.status === 'received' ? 'bg-purple-100 text-purple-800' :
                          item.status === 'ordered' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.status}
                        </span>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(item.budget_item_id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Budget Item' : 'Add Budget Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} required data-testid="budget-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="budget-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <Input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Cost</Label>
                  <Input type="number" min="0" step="0.01" value={form.estimated_cost} onChange={e => setForm({...form, estimated_cost: e.target.value})} required data-testid="budget-estimated-input" />
                </div>
                <div className="space-y-2">
                  <Label>Actual Cost</Label>
                  <Input type="number" min="0" step="0.01" value={form.actual_cost} onChange={e => setForm({...form, actual_cost: e.target.value})} data-testid="budget-actual-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-budget-item-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingItem ? 'Update' : 'Add Item'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
